import { logError } from "../../lib/logUtilities.js";
import { CCFieldDescriptors } from "./CCDescriptors";
import { CCStructure } from "./CCStructure";
import { CCButton, CCCollapser, CCIcon, CCJumper } from "./components/UI";
import { CCStructureItem } from "./types/structure";
import { CCButtonVariant } from "./types/variants";

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat.js";
dayjs.extend(customParseFormat); // enables dayjs(date, format, strict).isValid();

import { S3DirectoryPath, S3RootDirectory } from "@easycv/common/types/aws";
import { CCYearData, Conference } from "@easycv/common/types/conference";
import {
  ContextConstants,
  ContextMethods,
  ContextState
} from "@easycv/common/types/context";
import { CV } from "@easycv/common/types/cv";
import { User } from "@easycv/common/types/user";
import {
  Backend,
  CustomExportSettings,
  OverviewState,
  Section
} from "@easycv/common/types/util";
import { getCareerAvailableYears } from "../../lib/cvDataUtilities.js";
import { zeroPad } from "../../lib/dateHelpers.js";
import * as FileTransfer from "../../lib/fileTransfer";
import { escapeHtml } from "../../lib/frontendUtilities.js";
import { CCPermissions } from "./CCPermissions";
import { AbstractCCStructureItem } from "./CCStructureItem";
import { CCStyling, DefaultStyling } from "./CCStyling";
import {
  convertHTMLRichTextToXML,
  removeHTMLTags,
  trySortByKeyEnding
} from "./CCUtility";
import { CCText, CCWrapper } from "./components/Form";
import {
  CCAddHTMLEventInfo,
  CCAddHTMLRelative,
  CCAttachmentRules,
  CCCustomSections,
  CCIdentifiableError,
  CCSetupExtraOptions,
  CCSetupInterface,
  CCVerbage
} from "./types/setup";

export class CCSetup implements CCSetupInterface {
  // The user logged in, not necessarily the mentee or mentor.
  public user: User;
  // The cvOwner, usually the mentee
  public cvOwner: User;
  // The conference database entry
  public conference: Conference;
  // Backend instance
  public backend: Backend;
  // cvOwner's newDef
  public newDef: CV;
  // Current year's data, try not to use. Intead, access directly through newDef and viewYear: newDef.career[viewYear].data
  public yearData: CCYearData;
  // This cc's dataPrefix
  public dataPrefix: string;
  // The html root selector
  public ccRoot: string;
  // The current period being viewed, as a string (ie "01/01/2024 - 01/01/2025")
  public period: string;
  // Mentor list
  public mentorList: string[];
  // Conference's year info
  public yearInfo: { availableYears: number[]; baseYear: number };
  // Fields that will not trigger a `lastMenteeUpdate` on change
  public quietFields: string[];

  // State
  public contextState: ContextState;
  public contextConstants: ContextConstants;
  public contextMethods: ContextMethods;
  public overviewState?: OverviewState;
  public checkSave: ContextMethods["checkSave"];

  // Currently viewed year of the career conference. Example values: 2025, 2024, etc
  public viewYear: number;
  // Conference's start date
  public startDate: number;
  // Conference's end date
  public endDate: number;
  // Currently in use CCStructure instance
  public struct: CCStructure;

  // True for the first update of any year
  public uninitializedYearState: boolean;

  // Custom sections and their mappings. Set thru CCSetup.defineCustomSection()
  private customSections: CCCustomSections = {};
  // State
  private sectionState: Section.State;
  private section: string;

  private permissions: Readonly<CCPermissions>;

  private lastYearData: CCYearData;
  private isOpen: boolean;
  private mentors: { [key: string]: User };
  private cutoff: string;
  private month: string;
  private m_date: string;
  private updateCallback: (cc: CCSetup) => void;
  private verbage: CCVerbage;
  private erroredFields: Set<string>;
  private tooltips: { content: string; container: string }[];
  private cvOwnersMentorIds: string[];
  private independent: boolean;
  private notifyWhenUpdating: () => void;

  // Additional checks when changing status
  private statusChangeChecks: Required<
    CCSetupExtraOptions["statusChangeChecks"]
  >;
  private collapsibleSections: Record<string, JQuery>;
  private attachmentRules?: CCAttachmentRules;
  private attachmentDataKey?: string;
  private attachmentDirectory?: S3RootDirectory;
  private conditionallyOptionalFields: Record<string, [string, unknown]>;

  constructor(
    conference: Conference,
    backend: Backend,
    dataPrefix: string,
    contextMethods: ContextMethods,
    contextConstants: ContextConstants,
    contextState: ContextState,
    updateCallback?: (cc: CCSetup) => void,
    extraOptions: CCSetupExtraOptions = {
      ccRoot: "#ccRoot",
      independent: true,
      notifyWhenUpdating: () => {},
      statusChangeChecks: {}
    }
  ) {
    // same as `const { updateLayout, checkSave } = contextMethods;` etc...
    // just doing it like this so there aren't 500 lines of assigning properties to this class
    Object.assign(this, {
      ...contextMethods,
      ...contextConstants,
      ...contextState
    });
    this.uninitializedYearState = true;
    this.erroredFields = new Set();
    this.tooltips = [];
    this.contextState = contextState;
    this.contextConstants = contextConstants;
    this.contextMethods = contextMethods;
    this.updateCallback = updateCallback ?? (() => {});
    this.conference = conference;
    this.dataPrefix = dataPrefix;
    this.ccRoot = extraOptions.ccRoot;
    this.independent = extraOptions.independent;
    this.notifyWhenUpdating = extraOptions.notifyWhenUpdating;
    this.statusChangeChecks = {
      submitForReview:
        extraOptions.statusChangeChecks.submitForReview ?? (() => true),
      retractFromReview:
        extraOptions.statusChangeChecks.retractFromReview ?? (() => true),
      finalize: extraOptions.statusChangeChecks.finalize ?? (() => true),
      retractFinalize:
        extraOptions.statusChangeChecks.retractFinalize ?? (() => true)
    };

    this.collapsibleSections = {};
    this.backend = backend;
    this.conditionallyOptionalFields = {};

    for (const year of Object.keys(this.newDef.career)) {
      this.newDef.career[year].data ??= {};
      this.newDef.career[year].status ??= "mentee";
    }

    // Get mentor list for the user
    this.mentorList = Object.keys(this.conference.mentors).filter(
      (id) => this.conference.mentors[id][this.cvOwner.id] === true
    );

    this.updateYearInfo();

    this.setPermissions();

    this.period = this.getPeriod();

    this.verbage = {
      mentor: "Mentor",
      mentee: "Mentee",
      finalized: "Finalized",
      closed: "Closed"
    };

    this.cvOwnersMentorIds = [];
    for (const mentor of Object.keys(this.conference.mentors)) {
      if (Boolean(conference.mentors[mentor][this.cvOwner.id])) {
        this.cvOwnersMentorIds.push(mentor);
      }
    }

    // Clear out anything in the field list
    // #fieldList isn't literally "the list of fields", it's everything under the section heading
    // so #fieldList includes the export buttons, conference year switcher, etc...
    if (this.independent) {
      $("#fieldList").empty();
      $("#fieldList").append(
        `<div id="${this.ccRoot.replaceAll(/#/g, "")}" class="cc-root-class"></div>`
      );
    }

    window.addEventListener("resize", () => {
      this.updateCssSizing();
    });
  }

  public checkPermissions(): Readonly<CCPermissions> {
    return this.permissions;
  }
  private setPermissions() {
    try {
      this.permissions = Object.freeze(
        new CCPermissions(this.user, this.cvOwner, this.mentorList, this)
      );
    } catch (e) {
      logError(e);
      window.location.reload();
    }
  }
  /**
   * Override the words we use to describe things in the CC
   */
  public overrideVerbage(newVerbage: CCVerbage): void {
    this.verbage = newVerbage;
  }

  /**
   * Get the formated date period as a string (M/D/YY)
   *
   */
  public getPeriod(start?: string, end?: string): string {
    return `${dayjs(start ? start : this.startDate).format("M/D/YY")} - ${dayjs(end ? end : start ? "Ongoing" : this.endDate).format("M/D/YY")}`;
  }

  /**
   * Change the viewing year of the Career Conference.
   *
   * @param {int} offset - Can be positive or negative, -1 would move back one year, 1 would move forward one year.
   */
  public changeYear(offset: number = 0): void {
    const newYearIndex = this.overviewState.yearIndex + offset;
    if (
      newYearIndex >= 0 &&
      newYearIndex <= this.yearInfo.availableYears.length - 1
    ) {
      this.uninitializedYearState = true;
      this.overviewState.yearIndex = newYearIndex;
      this.updateYearInfo();
      this.updateDisplayedYear();
      this.update(this.struct.structure);
    }
  }

  /**
   *  Updates the text in the displayed year.
   */
  private updateDisplayedYear() {
    $(".careerYear").html(
      `<span>${this.getPeriod()}</span>` +
        (!this.isOpen || this.yearData.status === "closed"
          ? '<span class="glyphicon glyphicon-lock" style="margin-left: 10px;"></span>'
          : "")
    );

    $(".careerYearPrev").toggleClass(
      "invisible",
      (this.overviewState.yearIndex ?? 0) == 0
    );
    $(".careerYearNext").toggleClass(
      "invisible",
      (this.overviewState.yearIndex ?? 0) >=
        this.yearInfo.availableYears.length - 1
    );
  }

  /**
   * Add a button to either the header or the footer of the Career Conference
   *
   * @example
   *
   *    addButton('default', '<p></p>', "header", "careerYearNext", () => {});
   */
  public addButton(
    variant: CCButtonVariant,
    html: string,
    location: "header" | "footer" | string | CCAddHTMLRelative,
    buttonClass: string,
    clickHandler: () => void,
    css?: CCStyling
  ): void {
    let operationTarget: JQuery;
    if (location === "header") {
      if ($("#cc-header").length == 0)
        $(this.ccRoot).prepend(
          '<div id="cc-header" style="width: 100%"></div>'
        );
      operationTarget = $("#cc-header");
    } else if (location === "footer") {
      if ($("#cc-footer").length == 0)
        $(this.ccRoot).append('<div id="cc-footer"></div>');
      operationTarget = $("#cc-footer");
    } else if (
      location != undefined &&
      typeof location === "string" &&
      location.length > 0
    ) {
      operationTarget = $(location);
    } else if (
      typeof location === "object" &&
      location?.selector != undefined &&
      location?.relative != undefined
    ) {
      operationTarget = $(location.selector);
      let toAdd = CCButton(variant, buttonClass, html || "", css);
      let added: JQuery;
      if (location.relative === "append") {
        added = $(toAdd).appendTo(operationTarget);
      } else if (location.relative === "prepend") {
        added = $(toAdd).prependTo(operationTarget);
      } else if (location.relative === "before") {
        added = $(toAdd).insertBefore(operationTarget);
      } else if (location.relative === "after") {
        added = $(toAdd).insertAfter(operationTarget);
      }
      added.off("click").on("click", clickHandler);
      return;
    } else return;
    operationTarget.append(CCButton(variant, buttonClass, html || "", css));
    operationTarget
      .children(`.${buttonClass}`)
      .off("click")
      .on("click", clickHandler);
  }

  /**
   * Add HTML to the Career Conference
   *
   * @param {string} html - the HTML to add.
   * @param {{relative: ("before"|"prepend"|"append"|"after"), selector: string}} location - where the HTML will be added relative to the selector.
   * @param {{event: string, handler: Function}} [eventInfo] - an event or an array of events to listen for on the added HTML and what to do when those events occur.
   */
  public addHTML(
    html: string,
    location: CCAddHTMLRelative = {
      relative: "append",
      selector: this.ccRoot
    },
    eventInfo: CCAddHTMLEventInfo | CCAddHTMLEventInfo[] = {
      event: "",
      handler: () => {}
    }
  ): void {
    const operationTarget = $(location.selector);
    let added: JQuery;
    if (location.relative === "append") {
      added = $(html).appendTo(operationTarget);
    } else if (location.relative === "prepend") {
      added = $(html).prependTo(operationTarget);
    } else if (location.relative === "before") {
      added = $(html).insertBefore(operationTarget);
    } else if (location.relative === "after") {
      added = $(html).insertAfter(operationTarget);
    }
    if (Array.isArray(eventInfo)) {
      eventInfo.forEach((info) => {
        added.on(info.event, info.handler);
      });
    } else if (eventInfo.event?.length) {
      added.on(eventInfo.event, eventInfo.handler);
    }
  }

  /**
   * Generate HTML from the ConferenceStructure provided to the constructor
   *
   * @param {CCStructure} structure - the structure to generate.
   * @param {string} generateTo - selector that we append the generated HTML to.
   * @param {boolean} updateAfter - if we should call update after generation.
   *
   */
  public generateFieldsFromStructure(
    structure: CCStructure,
    generateTo: string = this.ccRoot,
    updateAfter: boolean = true
  ): void {
    this.struct = structure;
    // #printableContents is everything in ccRoot that is printable (exportable),
    // pretty much the fieldList minus everything that isn't going to be in the export
    const printableContents: JQuery = $('<span id="printableContents"></span>');
    // generate the HTML of the structure and append it to printableContents, then append all that to fieldList.
    $(generateTo).append(
      printableContents.append(this.struct.generateHTML(this.struct.structure))
    );
    this.hookInputFieldEvents();
    if (updateAfter) this.update(this.struct.structure);
  }

  /**
   * Define the behavior of a custom section.
   *
   * @param {string} customKey - the key of the custom section
   * @param {Function} createCallback - the function called when it is time for the section to be created. This function is passed the (container) that the custom section should be created in, and a reference to this career conference. This function can return anything that should be stored by the cc
   * @param {Function} statusCallback - the function called when it is time to check the status of the custom section.
   * @param {Function} errorCallback - the function called when it is time to check if there are errors in the section.
   */
  public defineCustomSection(
    customKey: string,
    createCallback: (container: JQuery, cc: CCSetup) => any,
    statusCallback: (data: any, cc: CCSetup) => void = () => {},
    errorCallback: () => CCIdentifiableError[] = () => undefined
  ): void {
    if (this.customSections[customKey] == undefined)
      this.customSections[customKey] = {
        data: createCallback($(`#CC-REPLACE-TARGET-${customKey}`), this),
        create: createCallback,
        status: statusCallback,
        error: errorCallback
      };
  }

  /**
   * Get the custom section data for a certain custom section's key
   */
  public customSectionData(key: string) {
    return this.customSections[key]?.data;
  }

  /**
   * Get all custom section data
   */
  public allCustomSectionData() {
    return Object.values(this.customSections).map((v) => v["data"]);
  }

  /**
   * Make a field conditionally optional
   * @param {string} fieldKey - The key of the field that should be made optional or not
   * @param {string} dependsOnOtherKey - The key to be conditional on
   * @param {unknown} beingValue - The value of dependsOnOtherKey to check
   */
  public makeFieldConditionallyOptional(
    fieldKey: string,
    dependsOnOtherKey: string,
    beingValue: unknown
  ) {
    this.conditionallyOptionalFields[fieldKey] = [
      dependsOnOtherKey,
      beingValue
    ];
  }

  /**
   * Set fields to not trigger a `lastMenteeUpdate` change
   */
  public addQuietFields(...quietFields: string[]) {
    this.quietFields ??= [];
    this.quietFields.push(...quietFields.map((f) => this.dataPrefix + f));
  }
  /**
   * Call an update to the career conference. This function needs to be looked at more closely in the future, it definitely does too much in some places and too little in others.
   */
  public update(items: CCStructureItem[]): void {
    this.checkSave(this.quietFields);
    this.updateCallback(this);
    this.setFieldStatus(items);
    this.updateStatusFooter();
    this.updateAttachmentButtons();
    this.updateCssSizing();
    this.refreshRichTextInstances();
    this.updateTooltips();
    this.notifyWhenUpdating();
    this.uninitializedYearState = false;
  }

  /**
   * Appropriately call .empty() on an element.
   * Takes care of clearing tooltips.
   */
  public emptyHTMLElement(element: JQuery<HTMLElement>) {
    element.find("[data-cc-tooltip-index]").each((_, eachElement) => {
      this.removeTooltip($(eachElement));
    });
    element.empty();
  }

  /**
   * Appropriately call .remove() on an element.
   * Takes care of clearing tooltips.
   */
  public removeHTMLElement(element: JQuery<HTMLElement>) {
    element.find("[data-cc-tooltip-index]").each((_, eachElement) => {
      this.removeTooltip($(eachElement));
    });
    this.removeTooltip(element);
    element.remove();
  }

  /**
   * Adds a tooltip to element
   */
  private addTooltip(
    element: JQuery<HTMLElement>,
    content: string,
    container?: string
  ) {
    const index = element.attr("data-cc-tooltip-index");
    // Only one tooltip per element for sanity
    if (index != undefined) {
      this.removeTooltip(element, index);
    }
    element.attr("data-cc-tooltip-index", this.tooltips.length);
    this.tooltips.push({ content, container });
    this.updateTooltips();
  }

  /**
   * Removes any tooltips from element
   */
  private removeTooltip(element: JQuery<HTMLElement>, tooltipIndex?: string) {
    const index = tooltipIndex ?? element.attr("data-cc-tooltip-index");
    const indexInt = parseInt(index);
    // This will only remove tooltips that CCSetup is managing
    if (
      index != undefined &&
      indexInt >= 0 &&
      indexInt < this.tooltips.length
    ) {
      element.removeAttr("data-cc-tooltip-index");
      this.tooltips.splice(indexInt, 1);
      $("[data-cc-tooltip-index]").each((_, eachElement) => {
        const v = parseInt(eachElement.dataset["ccTooltipIndex"]);
        if (v == undefined || Number.isNaN(v) || !Number.isFinite(v)) {
          delete eachElement.dataset["ccTooltipIndex"];
        } else if (v > indexInt) {
          eachElement.dataset["ccTooltipIndex"] = String(v - 1);
        }
      });
      element["popover"]?.("destroy");
    }
  }

  /**
   * Updates tooltips
   */
  private updateTooltips() {
    for (let i = 0; i < this.tooltips.length; ++i) {
      if (this.tooltips[i]) {
        const element = $(`[data-cc-tooltip-index="${i}"]`);
        if (element.length) {
          // Reset to no popover
          element["popover"]?.("destroy");
          element["popover"]?.({
            trigger: "hover",
            ...this.tooltips[i]
          });
        }
      }
    }
  }

  /**
   * Updates CSS sizing, only really used for multi ccs
   */
  private updateCssSizing() {
    // Native js dom functions instead of jquery for performance
    const aboveFormContent = document.querySelectorAll(
      `${this.ccRoot} > :not(#printableContents ~ *):not(#printableContents)`
    );
    for (const element of aboveFormContent) {
      if (element.classList.contains("cc-spacer")) continue;
      (element as HTMLElement).style.position = "fixed";
      (element as HTMLElement).style.zIndex = "1";
      (element as HTMLElement).style.backgroundColor = "#ffffff";
      let spacer = document.getElementById(`${element.id}-spacer`);
      if (spacer == null) {
        element.insertAdjacentHTML(
          "afterend",
          `<div id="${element.id}-spacer" class="cc-spacer"></div>`
        );
        spacer = document.getElementById(`${element.id}-spacer`);
      }
      spacer.style.height = element.getBoundingClientRect().height + "px";
    }
  }

  /**
   * Refresh tinyMCE rich text instances
   */
  private refreshRichTextInstances() {
    tinymce.remove(".richTextEnabled:not(td)");
    tinymce.init({
      selector: ".richTextEnabled:not(td)",
      inline: true,
      menubar: false,
      statusbar: false,
      plugins: "paste link",
      paste_word_valid_elements: "b,strong,i,u,em,br,sup,sub,p",
      toolbar:
        "bold italic underline subscript superscript | link unlink | removeformat",
      default_link_target: "_blank",
      entity_encoding: "raw",
      force_br_newlines: false,
      forced_root_block: "",
      force_p_newlines: false,
      formats: {
        underline: { inline: "u", exact: true }
      },
      setup: (editor: any) => {
        editor.on("change", (event: Event) => {
          $(editor.bodyElement).trigger("input");
          event.stopImmediatePropagation();
          event.stopPropagation();
        });
      }
    });
  }

  /**
   * Sets the value of key
   */
  public setValue<T extends string | number>(
    key: string,
    value: T,
    yearAgnostic: boolean = false
  ): void {
    $(`input[data-key="${key}"]`).first().val(value);

    if (yearAgnostic) {
      Object.keys(this.newDef.career).forEach((year) => {
        if (!isNaN(parseInt(year))) {
          this.newDef.career[year].data[this.dataPrefix + key] = value;
        }
      });
    } else {
      this.newDef.career[this.viewYear].data[this.dataPrefix + key] = value;
    }
  }

  /**
   * Sets the value of key if it is undefined or blank
   */
  public setValueIfUndefined<T extends string | number>(
    key: string,
    value: T
  ): void {
    this.newDef.career[this.viewYear].data[this.dataPrefix + key] ??= value;
    $(`input[data-key="${key}"]`)
      .first()
      .val(this.newDef.career[this.viewYear].data[this.dataPrefix + key]);
  }

  /**
   * Sets the value of key if predicate returns true
   */
  public setValueIfPredicate<T extends string | number>(
    key: string,
    value: T,
    predicate: (currentValue: T) => boolean
  ): void {
    const curr = this.newDef.career[this.viewYear].data[this.dataPrefix + key];
    if (predicate(curr)) {
      this.newDef.career[this.viewYear].data[this.dataPrefix + key] = value;
      $(`input[data-key="${key}"]`).first().val(value);
    }
  }

  /**
   * Populate a list of readonly values
   */
  public populateReadonlyValues(
    keyValuePairs: { key: string; value: any; yearAgnostic?: boolean }[]
  ): void {
    keyValuePairs.forEach((pair) => {
      $(`input[data-key="${pair.key}"]`).first().val(pair.value);
      if (pair.yearAgnostic) {
        Object.keys(this.newDef.career).forEach((year) => {
          if (!isNaN(parseInt(year))) {
            this.newDef.career[year].data[this.dataPrefix + pair.key] =
              pair.value;
          }
        });
      } else {
        this.newDef.career[this.viewYear].data[this.dataPrefix + pair.key] =
          pair.value;
      }
    });
  }

  /**
   * Adds 'jump to section' icons to the specified fields
   * Key of the object passed to this function should be the `data-key` attribute on the element to attach the button to
   * the value should be the tab key and optionally subtab key
   */
  public addSectionJumps(
    inputKeyToSection: Record<string, { tab: string; subtab?: string }>
  ) {
    for (const [key, { tab, subtab }] of Object.entries(inputKeyToSection)) {
      let element = $(`[data-key="${key}"]`);
      if (element.prop("tagName").toLowerCase() == "input") {
        element = element.parent();
      }
      element.append(CCJumper(key, tab, subtab));
    }
    $(".cc-jumper").on("click", (event) => {
      const to = event?.target?.dataset?.["section"];
      if (Boolean(to)) {
        this.contextState.section = to;
        const type = event.target.dataset?.["type"];
        if (Boolean(type)) {
          this.contextState.focusType = type;
        }
        this.contextMethods.refreshSection();
      }
    });
  }

  /**
   * Updates the style of jump buttons to match error states
   */
  private updateJumpersStyles() {
    $(".cc-jumper").each((_i, element) => {
      if (this.erroredFields.has(element?.dataset?.["key"])) {
        element.style.color = "#a94442";
      } else {
        element.style.color = "";
      }
    });
  }

  /**
   * Adds a Collapse button to an element [from] and collapses until [to]
   * when that button is pressed.
   * @param {{ from: string; to: string; }} range - The start and end of the collapsable range. Both should be unique JQuery selectors
   */
  public setCollapseRange(range: { from: string; to: string }) {
    const fromElement = $(range.from);
    fromElement.append(CCCollapser());
    this.collapsibleSections[range.from] = fromElement.nextUntil(range.to);
    fromElement
      .children()
      .last()
      .on("click", () => {
        if (fromElement.children().last().text() === "Collapse") {
          this.collapsibleSections[range.from]?.hide();
          fromElement.children().last().text("Expand");
        } else {
          this.collapsibleSections[range.from]?.show();
          fromElement.children().last().text("Collapse");
        }
      });
  }

  /**
   * Calls setCollapseRange with each passed argument
   */
  public setCollapseRanges(...ranges: { from: string; to: string }[]) {
    for (const r of ranges) this.setCollapseRange(r);
  }

  /**
   * Get a list of escaped mentor names for the user.
   *
   * @returns {string[]} Array of escaped mentor names.
   */
  public getMentorNames(): string[] {
    return this.mentorList.map((mentorId) =>
      escapeHtml(this.mentors[mentorId]?.name)
    );
  }

  /**
   * Updates yearInfo, overviewState, viewYear, etc
   */
  private updateYearInfo(): void {
    this.yearInfo = getCareerAvailableYears(
      this.newDef,
      this.conference,
      this.user,
      this.cvOwner
    );
    if (typeof this.yearInfo.baseYear === "string") {
      this.yearInfo.baseYear = parseInt(this.yearInfo.baseYear);
    }

    // Update overview state
    this.overviewState = this.sectionState[this.section];
    if (
      this.overviewState == null ||
      !this.yearInfo.availableYears[this.overviewState.yearIndex]
    ) {
      // Set default if null
      this.sectionState[this.section] = this.overviewState = {
        yearIndex: this.yearInfo.availableYears.indexOf(this.yearInfo.baseYear)
      };
    }

    this.viewYear = this.yearInfo.availableYears[this.overviewState.yearIndex];

    this.cutoff = this.conference.cutoffDate || "1/1"; // Before this = last year. New section starts on this date
    this.month = this.cutoff.split("/")[0] || "1";
    this.m_date = this.cutoff.split("/")[1] || "1";
    this.startDate = new Date(
      this.month + "/" + this.m_date + "/" + this.viewYear
    ).getTime();
    this.endDate =
      new Date(
        this.month + "/" + this.m_date + "/" + (this.viewYear + 1)
      ).getTime() -
      1000 * 60 * 60 * 24;

    this.isOpen =
      this.conference.openYears[
        this.viewYear
      ]?.open /*|| this.editAccessOverride*/;

    this.yearData = this.newDef.career[this.viewYear];
    if (this.yearData == null) {
      // Set default if null
      this.newDef.career[this.viewYear] = this.yearData = {
        timeBreakdown: {},
        goals: this.conference.goals,
        status: "mentee",
        questions: {},
        mentorComments: "",
        details: {},
        data: {}
      };
    }
    this.lastYearData = this.newDef.career[this.viewYear - 1];
    if (this.lastYearData == null) {
      // Set default if null
      this.newDef.career[this.viewYear - 1] = this.lastYearData = {
        timeBreakdown: {},
        goals: this.conference.goals,
        status: "mentee",
        questions: {},
        mentorComments: "",
        details: {},
        data: {}
      };
    }
  }

  /**
   * Get attachmentRules depending on the value of this.viewingAs
   */
  private getRelevantAttachmentRules() {
    return this.checkPermissions().readableAsMentorIdentity()
      ? this.attachmentRules.mentor
      : this.checkPermissions().readableAsMenteeIdentity()
        ? this.attachmentRules.mentee
        : null;
  }

  /**
   * Check the validity of attachmentRules, attachmentDataKey, and the result of getRelevantAttachmentRules()
   */
  private invalidAttachmentStateCheck() {
    return (
      this.attachmentRules == undefined ||
      this.attachmentDataKey == undefined ||
      this.getRelevantAttachmentRules() == null
    );
  }

  /**
   * Update the remove attachment button
   */
  private updateRemoveAttachmentButton() {
    if (this.invalidAttachmentStateCheck()) return;
    const rules = this.getRelevantAttachmentRules();
    const attachmentValue =
      this.newDef?.career?.[this.viewYear]?.data?.[this.attachmentDataKey];
    const remove = $(".__removeAttachedDocument");
    remove.css(
      "display",
      rules.remove && attachmentValue != undefined ? "inline-block" : "none"
    );
    remove.off("click");
    if (rules.remove && attachmentValue) {
      remove.on("click", async () => {
        FileTransfer.deleteS3Object(this.backend, {
          directory: this.attachmentDirectory,
          path: this.getAttachmentPathBasedOnDirectory(),
          filename: attachmentValue
        })
          .then(() => {
            delete this.newDef.career[this.viewYear].data?.[
              this.attachmentDataKey
            ];
            this.checkSave(this.quietFields);
            this.updateAttachmentButtons();
          })
          .catch((err) => {
            logError(err);
            alert("Error deleting attachment");
          });
      });
    }
  }

  /**
   * Update the download attachment button
   */
  private updateDownloadAttachmentButton() {
    if (this.invalidAttachmentStateCheck()) return;
    const rules = this.getRelevantAttachmentRules();
    const attachmentValue =
      this.newDef?.career?.[this.viewYear]?.data?.[this.attachmentDataKey];
    const download = $(".__downloadAttachedDocument");
    download.css(
      "display",
      rules.download && attachmentValue != undefined ? "inline-block" : "none"
    );
    download.off("click");
    if (rules?.download && attachmentValue) {
      download.on("click", async () => {
        await FileTransfer.Download(this.backend, {
          directory: this.attachmentDirectory,
          path: this.getAttachmentPathBasedOnDirectory(),
          filename: attachmentValue
        });
      });
    }
  }

  /**
   * Get an S3DirectoryPath based on this.attachmentDirectory
   */
  private getAttachmentPathBasedOnDirectory(): S3DirectoryPath<
    typeof this.attachmentDirectory
  > {
    if (this.attachmentDirectory === "stanford-apr") {
      return [this.conference.id, this.cvOwner.id, String(this.viewYear)];
    }
  }

  /**
   * Update the add attachment button
   */
  private updateAttachButton() {
    if (this.invalidAttachmentStateCheck()) return;
    const rules = this.getRelevantAttachmentRules();
    const attach = $(".__attachExternalDocument");
    const attachmentValue =
      this.newDef?.career?.[this.viewYear]?.data?.[this.attachmentDataKey];
    attach.css("display", rules.upload ? "inline-block" : "none");
    attach.off("click");
    if (rules?.upload) {
      attach.on("click", () => {
        attach
          .siblings("form")
          .children("input")
          .trigger("click")
          .on("change", async (e) => {
            const file = e.target.files[0];
            if (file == undefined) {
              alert("No file selected.");
              return;
            }
            const wrapper = $("#__attachExternalDocumentWrapper");
            wrapper
              .parent()
              .after(
                `<div id="progress-wrapper" style="top:0;position:absolute;"><progress style="position: fixed;z-index:2;"></progress></div>`
              );
            const progress = wrapper
              .parent()
              .siblings("#progress-wrapper")
              .children("progress");

            const wrapperRect = wrapper.get(0).getBoundingClientRect();
            progress.css("top", `${wrapperRect.bottom}px`);
            progress.css("left", `${wrapperRect.left}px`);
            progress.css("width", `${wrapperRect.width}px`);
            const response = await FileTransfer.Upload(
              this.backend,
              {
                directory: this.attachmentDirectory,
                path: this.getAttachmentPathBasedOnDirectory(),
                filename: file.name
              },
              file
            );
            progress.css("transition-duration", "500ms");
            progress.css("transition-property", "opacity");
            progress.css("opacity", "0");
            setTimeout(() => {
              progress.remove();
            }, 501);
            if (response.status == 200) {
              this.newDef.career[this.viewYear].data[this.attachmentDataKey] =
                file.name;
              this.checkSave(this.quietFields);
              this.updateDownloadAttachmentButton();
              this.updateRemoveAttachmentButton();
            }
          });
      });
    }
  }

  /**
   * Calls this.updateAttachButton(), this.updateDownloadAttachmentButton(), this.updateRemoveAttachmentButton()
   */
  private updateAttachmentButtons() {
    this.updateAttachButton();
    this.updateDownloadAttachmentButton();
    this.updateRemoveAttachmentButton();
  }

  /**
   * Add buttons that allow users to attach a document and later download that same document
   * @param location - Location of the buttons
   * @param attachmentDataKey - Key of relevant document data stored in the cc (data needed to access S3 document)
   */
  public appendAddAttachmentButton(
    attachmentDirectory: S3RootDirectory,
    location: CCAddHTMLRelative,
    attachmentDataKey: string,
    attachmentRules: CCAttachmentRules = {
      mentee: { upload: true, download: true, remove: true },
      mentor: { upload: true, download: true, remove: true }
    },
    {
      attach = "Attach Document",
      download = "Download Attached Document",
      remove = "Remove Attached Document",
      downloadHeader = "Download Attached Document"
    } = {},
    forceIncludeDownloadButtonInHeader = false
  ) {
    this.attachmentDirectory = attachmentDirectory;
    this.attachmentRules = attachmentRules;
    this.attachmentDataKey = attachmentDataKey;
    this.addHTML(
      `<div style="margin-bottom: 10px; width: min(1020px, 100%);"><div style="display: inline-flex; flex-direction:column;margin-right:15px" id="__attachExternalDocumentWrapper">` +
        CCButton(
          "primary",
          "__attachExternalDocument",
          CCIcon("upload") + `<span>${attach}</span>`,
          CCStyling.fromObject({ display: "none", "margin-right": "0px" })
        ) +
        `<form id="__attachExternalDocumentForm" enctype="multipart/form-data" style="display: none;"><input type="file" style="display: none;"/></form>` +
        `</div></div>`,
      location
    );
    this.addButton(
      "primary",
      CCIcon("download") + `<span>${download}</span>`,
      { relative: "after", selector: "#__attachExternalDocumentWrapper" },
      "__downloadAttachedDocument",
      () => {},
      CCStyling.fromObject({ display: "none" })
    );
    this.addButton(
      "primary",
      CCIcon("remove") + `<span>${remove}</span>`,
      { relative: "after", selector: ".__downloadAttachedDocument" },
      "__removeAttachedDocument",
      () => {},
      CCStyling.fromObject({ display: "none" })
    );

    if (forceIncludeDownloadButtonInHeader) {
      this.addButton(
        "primary",
        CCIcon("download") + `<span>${downloadHeader}</span>`,
        { relative: "append", selector: "#cc-header" },
        "__downloadAttachedDocument",
        () => {},
        CCStyling.fromObject({ display: "none" })
      );
    }
  }

  /**
   * Appends the standard year switch buttons and year display.
   */
  public appendStandardHeaderYearSwitcher() {
    this.addButton(
      "default",
      CCIcon("arrow-left"),
      "header",
      "careerYearPrev",
      () => this.changeYear(-1)
    );
    this.addHTML(
      CCText("h4", "careerYear", this.period, DefaultStyling.careerYear),
      {
        relative: "after",
        selector: ".careerYearPrev"
      }
    );
    this.addButton(
      "default",
      CCIcon("arrow-right"),
      "header",
      "careerYearNext",
      () => this.changeYear(+1)
    );
    this.updateDisplayedYear();
  }

  /**
   * Appends the following buttons to the form:
   * Date Limited CV, Full CV, CC Download, Print
   * Labels can be customized with args to this function
   * Calls provided callback when the CC Download button is pressed
   */
  public appendStandardHeaderButtons(
    ccDownloadCallback: () => void,
    {
      dateLimited = "Date Limited CV",
      fullCv = "Full CV",
      ccDownload = "Career Conference",
      print = "Print"
    } = {},
    includePrint: boolean = true
  ) {
    this.addButton(
      "primary",
      CCIcon("download") + `<span>${dateLimited}</span>`,
      "header",
      "downloadDateLimited",
      () => this.dateLimitedCVExport(this.contextState.customExportSettings)
    );
    this.addButton(
      "primary",
      CCIcon("download") + `<span>${fullCv}</span>`,
      "header",
      "downloadFull",
      () => this.fullCVExport(this.contextState.customExportSettings)
    );
    this.addButton(
      "primary",
      CCIcon("download") + `<span>${ccDownload}</span>`,
      "header",
      "downloadDataSummary",
      ccDownloadCallback
    );
    if (includePrint) {
      this.addButton(
        "primary",
        CCIcon("print") + `<span>${print}</span>`,
        "header",
        "downloadPrint",
        () => this.printExport("Title")
      );
    }
  }

  /**
   * calls CCSetup.appendStandardHeaderYearSwitcher
   * followed by CCSetup.appendStandardHeaderButtons
   *
   * arguments provided to this function are passed to CCSetup.appendStandardHeaderButtons
   */
  public appendStandardHeader(
    ccDownloadCallback: () => void,
    {
      dateLimited = "Date Limited CV",
      fullCv = "Full CV",
      ccDownload = "Career Conference",
      print = "Print"
    } = {},
    includePrint: boolean = true
  ) {
    this.appendStandardHeaderYearSwitcher();
    this.appendStandardHeaderButtons(
      ccDownloadCallback,
      {
        dateLimited,
        fullCv,
        ccDownload,
        print
      },
      includePrint
    );
  }

  /**
   * Date limited CV Export
   *
   * this function is never called by this class on it's own. It is here to use as a click event handler
   *
   * @param {object} customExportSettings - the customExportSettings object.
   */
  public dateLimitedCVExport(customExportSettings: CustomExportSettings): void {
    customExportSettings.dateLimited = true;
    customExportSettings.shown = true;
    $("#m1").val(this.month);
    $("#m2").val(dayjs(this.endDate).month() + 1);
    $("#d1").val(this.m_date);
    $("#d2").val(dayjs(this.endDate).date());
    $("#y1").val(this.viewYear);
    $("#y2").val(dayjs(this.endDate).year());
    $(".download").trigger("click");
  }

  /**
   * Full CV Export
   *
   * this function is never called by this class on it's own. It is here to use as a click event handler
   *
   * @param {object} customExportSettings - the customExportSettings object.
   */
  public fullCVExport(customExportSettings: CustomExportSettings): void {
    customExportSettings.dateLimited = false;
    $(".download").trigger("click");
  }

  /**
   * Print export
   *
   * this function is never called by this class on it's own. It is here to use as a click event handler
   *
   * @param {string} documentTitle - the title of the document.
   *
   */
  public printExport(documentTitle: string): void {
    const dontPrintThese: JQuery = $(".noPrint");
    dontPrintThese.hide();
    const printContents: string = $("#printableContents")[0].innerHTML;
    dontPrintThese.show();
    const w = window.open("", "Test");
    if (w == null) return;
    w.document.write('<link rel="stylesheet" href="/css/bootstrap.min.css"/>');
    w.document.write(
      "<style> @media print {  @page { margin: 1in; } } </style>"
    );
    const yearRange: string = $(".careerYear").text();
    w.document.write(
      `<div style="padding: 15px;">` +
        `<h4 style="">${yearRange} ${this.cvOwner.name || ""} ${documentTitle}</h4>` +
        printContents +
        `</div>`
    );
    setTimeout(function () {
      w.print();
    }, 1000);
  }

  /**
   * Career Conference export
   *
   * this function is never called by this class on it's own. It is here to use as a click event handler
   * if provided, callback is called after oReq.onload.
   *
   */
  public conferenceExport(
    templateName: string,
    downloadName: string,
    dataOverrides?: { [key: string]: any },
    replaceUndefinedWith?: (part: DXT.Part) => any,
    font?: DXT.OutputFontOptions,
    callback?: () => void
  ) {
    const template = "/templateFiles/reports/" + templateName;
    const oReq = new XMLHttpRequest();
    oReq.open("GET", template, true);
    oReq.responseType = "arraybuffer";
    oReq.onload = async () => {
      const yearData = this.newDef.career[this.viewYear].data || {};
      const dataKeys = Object.keys(yearData).filter(
        (key) => key.startsWith(this.dataPrefix) && yearData[key]
      );
      const data = {};
      dataKeys.forEach((key) => {
        if (typeof yearData[key] == "object") {
          data[key.substring(this.dataPrefix.length)] = [
            ...Object.keys(yearData[key])
              .filter((objectKey) => yearData[key][objectKey] != null)
              .sort(trySortByKeyEnding)
              .map((objectKey) => yearData[key][objectKey])
          ];
        } else {
          data[key.substring(this.dataPrefix.length)] = yearData[key];
        }
      });
      if (dataOverrides != undefined) {
        const overrideParsed = {};
        for (const key of Object.keys(dataOverrides)) {
          if (typeof dataOverrides[key] === "object") {
            overrideParsed[key] = [
              ...Object.keys(dataOverrides[key])
                .filter((subKey) => dataOverrides[key][subKey] != null)
                .sort(trySortByKeyEnding)
                .map((subKey) => dataOverrides[key][subKey])
            ];
          } else {
            overrideParsed[key] = dataOverrides[key];
          }
        }
        for (const key of Object.keys(overrideParsed)) {
          data[key] = overrideParsed[key];
        }
      }

      const arrayBuffer = oReq.response;
      const content = new Uint8Array(arrayBuffer);
      const zip = new PizZip(content);
      const doc = new Docxtemplater();
      doc.loadZip(zip);

      // "Relationships" are links to things. Word requires hyperlinks to the internet to be in the rels file.
      const relationships: string = doc
        .getZip()
        .file("word/_rels/document.xml.rels")
        .asText();
      // Extract current "default" relationship ids, so we know what Ids we can and can't use.
      let relationshipMatch: RegExpExecArray;
      let relationshipIds = [];
      let individualRelationships = [];
      const relationshipRegex = /<Relationship .+?>/g;
      while (
        (relationshipMatch = relationshipRegex.exec(relationships)) != undefined
      ) {
        individualRelationships.push(relationshipMatch[0]);
        const id = relationshipMatch[0].match(/Id=(['"])rId(.+?)\1/);
        relationshipIds.push(id[id.length - 1]);
      }
      individualRelationships = individualRelationships.filter(Boolean);
      relationshipIds = relationshipIds.filter(Boolean);

      let firstIdWeCanAdd = Math.max(...relationshipIds) + 1;
      const addedLinks = [];
      const docxOpts: DXT.Options = {
        linebreaks: true,
        parser: (tag: string): DXT.Parser => {
          return {
            get: (scope: any, context: DXT.ParserContext): any => {
              // If tag in the template file is a raw xml tag, ({@variable})
              if (context.meta.part.module == "rawxml") {
                // Convert our data, save links
                const converted = convertHTMLRichTextToXML(
                  scope[tag],
                  firstIdWeCanAdd,
                  font
                );
                if (converted.links.length > 0) {
                  addedLinks.push(...converted.links);
                  firstIdWeCanAdd += converted.links.length;
                }
                return converted.xml;
              }
              // Else, default behavior
              return typeof scope[tag] === "string"
                ? removeHTMLTags(scope[tag])
                : scope[tag];
            }
          };
        }
      };
      if (replaceUndefinedWith != undefined) {
        docxOpts.nullGetter = replaceUndefinedWith;
      }
      doc.setOptions(docxOpts);

      doc.setData(data);
      try {
        doc.render();
      } catch (error) {
        console.log({
          message: error.message,
          name: error.name,
          stack: error.stack,
          properties: error.properties
        });
        throw error;
      }
      // Add relationships for any links we have
      const prologue = relationships.substring(
        0,
        relationships.indexOf("<Relationship ")
      );
      const epilogue = "\n</Relationships>";
      const addedRelationships = addedLinks.map((addedLink) => {
        return (
          `<Relationship Id="rId${addedLink.id}" ` +
          `Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/hyperlink" ` +
          `Target="${addedLink.link}" ` +
          `TargetMode="External"` +
          `/>`
        );
      });
      const allRelationships =
        individualRelationships.join("") + addedRelationships.join("");
      doc
        .getZip()
        .file(
          "word/_rels/document.xml.rels",
          prologue + allRelationships + epilogue
        );
      const dateString =
        new Date().getFullYear() +
        1900 +
        "-" +
        zeroPad(new Date().getMonth() + 1, 2) +
        "-" +
        zeroPad(new Date().getDate(), 2);
      const docFile = doc.getZip().generate({
        type: "blob",
        mimeType:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      });

      const fileName =
        this.cvOwner.name
          .split(",")
          .join("")
          .split(".")
          .join("")
          .split(" ")
          .join("_") +
        "_" +
        dateString +
        downloadName.replaceAll(/ /g, "_") +
        ".docx";

      this.backend.send("saveLog", {
        id: this.user.id,
        path: window.location.pathname,
        action: JSON.stringify({
          type: "download " + downloadName,
          title: fileName.split("\n").join("")
        }),
        context: JSON.stringify({
          agent: window.navigator.userAgent,
          params: window.location.search,
          cvId: this.newDef.id,
          cvOwner: this.cvOwner.id
        })
      });

      saveAs(docFile, fileName.split("\n").join(""));

      callback?.();
    };
    oReq.send();
  }

  private setFieldStatus(items: CCStructureItem[]): void {
    const yearData = this.newDef.career[this.viewYear];
    const data = yearData.data;
    const instantiated = this.struct.instantiateItems(items);
    for (const instance of instantiated) {
      if (AbstractCCStructureItem.itemIsInput(instance)) {
        if (this.conditionallyOptionalFields[instance.key] != undefined) {
          const [dependsOnOtherKey, beingValue] =
            this.conditionallyOptionalFields[instance.key];
          if (data[this.dataPrefix + dependsOnOtherKey] === beingValue) {
            instance.optional = true;
          }
        }

        let dataEntry = String(data[this.dataPrefix + instance.key] ?? "");
        const isError = instance.updateWithData(dataEntry);

        const element = instance.element();
        const setErrorOn = element.hasClass("cc-radio-wrapper")
          ? element
          : element.parent();
        if (isError) {
          this.erroredFields.add(instance.key);
          setErrorOn.addClass("has-error");
        } else {
          this.erroredFields.delete(instance.key);
          setErrorOn.removeClass("has-error");
        }
        instance.refreshPermissions(this.checkPermissions());
      } else {
        // hyperlink, instructions, custom, grid, header
        if (AbstractCCStructureItem.itemIsGrid(instance)) {
          this.setFieldStatus(instance.sections);
        } else if (AbstractCCStructureItem.itemIsCustom(instance)) {
          if (this.customSections[instance.key] != undefined) {
            this.customSections[instance.key].status(
              this.customSections[instance.key].data,
              this
            );
            $(`[data-key="${instance.key}"]`)
              .find("*.has-error")
              .toggleClass("has-error", false);
            const errorResult =
              this.customSections[instance.key]?.error?.() ?? [];
            let anyError = false;
            for (const e of errorResult) {
              const element = $(e.uniqueJquerySelector);
              if (e.isError) {
                if (e.tooltip != undefined) {
                  this.addTooltip(
                    element,
                    e.tooltip,
                    `[data-key='${instance.key}']`
                  );
                }
                anyError = true;
                this.erroredFields.add(instance.key);
                element.toggleClass("has-error", true);
              } else {
                this.removeTooltip(element);
                element.toggleClass("has-error", false);
              }
            }
            if (errorResult.length == 0 || !anyError) {
              this.erroredFields.delete(instance.key);
            }
          }
        }
      }
    }
  }

  /**
   * Force an error in a field.
   * State = true = has an error,
   * State = false = has no error.
   */
  public forceErrorState(selector: string, state: boolean) {
    const element = $(selector);
    const key = element.closest("[data-key]").attr("data-key");
    element.parent().toggleClass("has-error", state);
    if (state) {
      if (!this.erroredFields.has(key)) {
        this.erroredFields.add(key);
        this.updateStatusFooter();
      }
    } else {
      if (this.erroredFields.delete(key)) {
        this.updateStatusFooter();
      }
    }
  }

  private hookInputFieldEvents() {
    Object.keys(CCFieldDescriptors).forEach((fieldKey: string) => {
      const elements: JQuery = $(`.conf${fieldKey}`);
      const field = CCFieldDescriptors[fieldKey];
      // Remove any current change listeners
      elements.off(field.eventType);
      // Add change listener
      elements.on(field.eventType, (event) => {
        const target = $(event.target);
        const key = target.closest("[data-key]").attr("data-key");
        const { isError, value } = field.errorCheck(
          target,
          field.getValue(target)
        );
        if (target.hasClass("yearAgnostic")) {
          Object.keys(this.newDef.career).forEach((year) => {
            if (!isNaN(parseInt(year))) {
              this.newDef.career[year].data[this.dataPrefix + key] = value;
            }
          });
        } else {
          this.newDef.career[this.viewYear].data[this.dataPrefix + key] = value;
        }
        if (isError) {
          if (!this.erroredFields.has(key)) {
            this.erroredFields.add(key);
            target.parent().addClass("has-error");
            this.updateStatusFooter();
          }
        } else {
          if (this.erroredFields.delete(key)) {
            target.parent().removeClass("has-error");
            this.updateStatusFooter();
          }
        }
        this.backend.send(
          "log",
          `q - ${key} - ${fieldKey} - ${value} - user ${(this.user || {}).id}`
        );
        for (const [condK, [k, _]] of Object.entries(
          this.conditionallyOptionalFields
        )) {
          if (k === key) {
            this.setFieldStatus(
              this.struct.structure.filter((i) => i["key"] === condK)
            );
            this.updateStatusFooter();
          }
        }
        this.checkSave(this.quietFields);
      });
    });
  }

  private updateStatusFooter(): void {
    const statusTitle = $(".statusTitle");
    if (statusTitle.length) {
      let statusText = "Status: ";
      if (this.verbage.statusTextFn != undefined) {
        statusTitle.text(
          statusText +
            this.verbage.statusTextFn(
              this.isOpen ? this.yearData.status : "closed",
              this.verbage
            )
        );
      } else {
        if (this.isOpen) {
          statusText += this.verbage[this.yearData.status];
        } else {
          statusText += this.verbage.closed;
        }
        statusTitle.text(statusText + " Editing");
      }
    }
    const { nextStatus, prevStatus } = {
      nextStatus: $(".nextStatus"),
      prevStatus: $(".prevStatus")
    };
    if (nextStatus.length && prevStatus.length) {
      nextStatus.hide();
      prevStatus.hide();
      if (this.isOpen) {
        if (this.yearData.status === "mentee") {
          if (
            this.checkPermissions().statusChangeableAsMenteeIdentity() &&
            this.statusChangeChecks.submitForReview()
          ) {
            nextStatus
              .show()
              .text("Submit for Review")
              .toggleClass("disabled", Boolean(this.erroredFields.size));
          }
        } else if (this.yearData.status === "mentor") {
          if (
            this.checkPermissions().statusChangeableAsMenteeIdentity() &&
            this.statusChangeChecks.retractFromReview()
          ) {
            prevStatus.show().text("Resume Editing");
          }
          if (
            this.checkPermissions().statusChangeableAsMentorIdentity() &&
            this.statusChangeChecks.finalize()
          ) {
            nextStatus
              .show()
              .text("Finalize Review")
              .toggleClass("disabled", Boolean(this.erroredFields.size));
          }
        } else if (this.yearData.status === "finalized") {
          if (
            this.checkPermissions().statusChangeableAsMentorIdentity() &&
            this.statusChangeChecks.retractFinalize()
          ) {
            prevStatus.show().text("Resume Review");
          }
        }
      }
    }
    const signSection = $(".signSection");
    if (signSection.length) {
      if (this.yearData.status === "finalized") {
        signSection.show();
        const data = this.newDef.career[this.viewYear].data;
        const menteeSign = (data[this.dataPrefix + "menteeSign"] ??= {});
        const mentorSign = (data[this.dataPrefix + "mentorSign"] ??= {});
        const style = CCStyling.join(
          DefaultStyling.text,
          CCStyling.fromObject({ width: "auto" })
        );
        if (menteeSign.id == null) {
          $(".menteeSign").text("(Not Yet Signed)");
        } else {
          $(".menteeSign").text(
            `Signed by ${menteeSign.signature} on ${dayjs(menteeSign.time).format("MM/DD/YYYY")} at ${dayjs(menteeSign.time).format("h:mm A")}`
          );
        }
        if (
          this.user.id != this.cvOwner.id ||
          this.yearData.status !== "finalized"
        ) {
          $(".menteeSign")
            .css(style.css)
            .removeClass("btn-primary")
            .removeClass("btn");
        } else if (menteeSign.id == null) {
          $(".menteeSign").text("Digitally Sign");
        }
        if (mentorSign.id == null) {
          $(".mentorSign").text("(Not Yet Signed)");
        } else {
          $(".mentorSign").text(
            `Signed by ${mentorSign.signature} on ${dayjs(mentorSign.time).format("MM/DD/YYYY")} at ${dayjs(mentorSign.time).format("h:mm A")}`
          );
        }
        if (
          !this.cvOwnersMentorIds.includes(this.user.id) ||
          this.yearData.status !== "finalized"
        ) {
          $(".mentorSign")
            .css(style.css)
            .removeClass("btn-primary")
            .removeClass("btn");
        } else if (mentorSign.id == null) {
          $(".mentorSign").text("Digitally Sign");
        }
      } else {
        signSection.hide();
        const data = this.newDef.career[this.viewYear].data;
        data[this.dataPrefix + "menteeSign"] = {};
        data[this.dataPrefix + "mentorSign"] = {};
      }
    }
    this.updateJumpersStyles();
  }

  /**
   * Updates career year's statusTimes[status] to be the current time
   */
  private updateStatusTimes() {
    this.newDef.career[this.viewYear].statusTimes ??= {};
    this.newDef.career[this.viewYear].statusTimes[this.yearData.status] =
      Date.now();
  }
  /**
   * Append default status buttons to the bottom of the page.
   */
  public appendStatusButtons(
    includeSignSection: boolean = true,
    disableEmails: boolean = false
  ): void {
    this.addHTML(CCWrapper("", "statusButtons"), {
      relative: "append",
      selector: this.ccRoot
    });
    this.addHTML(
      CCText(
        "h4",
        "statusTitle",
        `Status: ${this.verbage[this.yearData.status]}`
      ),
      {
        relative: "append",
        selector: ".statusButtons"
      }
    );
    this.addHTML(
      CCButton("primary", "nextStatus", ""),
      {
        relative: "append",
        selector: ".statusButtons"
      },
      {
        event: "click",
        handler: () => {
          const oldStatus = this.yearData.status;
          if ($(".nextStatus").hasClass("disabled")) {
            let firstInSet = [...this.erroredFields][0];
            const scrollToElement = $(
              firstInSet != undefined
                ? `[data-key="${firstInSet}"]`
                : this.ccRoot
            )[0];
            const isVisible = scrollToElement.checkVisibility();
            if (!isVisible) {
              // Check if not visible because it is in a collapsed section
              for (const collapseKey of Object.keys(this.collapsibleSections)) {
                if (this.collapsibleSections[collapseKey].is(scrollToElement)) {
                  // Expand that section before scrolling so the error is identifiable to the user
                  $(collapseKey).find(".cc-collapser").trigger("click");
                  break;
                }
              }
            }
            scrollToElement.scrollIntoView();
            return;
          }
          if (this.yearData.status === "mentee") {
            const insert = this.verbage.alertTextFn
              ? this.verbage.alertTextFn(this.yearData.status, this.verbage)
              : `your ${this.verbage.mentor.toLowerCase()}`;
            const notifText = disableEmails
              ? ""
              : ` This will send a notification to ${insert} that you have completed this form.`;
            const check = confirm(
              `Are you sure you are ready to submit?${notifText}`
            );
            if (!check) return;
            if (!disableEmails) {
              this.backend.send("sendEmail", {
                type: "uvaMedMentorAlert",
                data: {
                  mentorList: this.mentorList,
                  cvOwnerId: this.cvOwner.id
                }
              });
            }
            this.yearData.status = "mentor";
          } else if (this.yearData.status === "mentor") {
            const insert = this.verbage.alertTextFn
              ? this.verbage.alertTextFn(this.yearData.status, this.verbage)
              : `your ${this.verbage.mentee.toLowerCase()}`;
            const notifText = disableEmails
              ? ""
              : ` This will send a notification to ${insert} that you have completed this review.`;
            const check = confirm(
              `Are you sure you are ready to finalize?${notifText}`
            );
            if (!check) return;
            this.yearData.status = "finalized";
            if (!disableEmails) {
              this.backend.send("sendEmail", {
                type: "uvaMedMenteeAlert",
                data: {
                  mentorId: this.user.id,
                  cvOwnerId: this.cvOwner.id
                }
              });
            }
            const data = this.newDef.career[this.viewYear].data;
            data[this.dataPrefix + "cclib_finalized_timestamp"] = Date.now();
          }
          if (this.yearData.status !== oldStatus) {
            this.updateStatusTimes();
          }
          this.update(this.struct.structure);
        }
      }
    );
    this.addHTML(
      CCButton("primary", "prevStatus", ""),
      {
        relative: "append",
        selector: ".statusButtons"
      },
      {
        event: "click",
        handler: () => {
          const oldStatus = this.yearData.status;
          if (this.yearData.status === "mentor") {
            this.yearData.status = "mentee";
          } else if (this.yearData.status === "finalized") {
            this.yearData.status = "mentor";
          }
          if (this.yearData.status !== oldStatus) {
            this.updateStatusTimes();
          }
          this.update(this.struct.structure);
        }
      }
    );
    if (includeSignSection) {
      this.addHTML(CCWrapper("", "signSection"), {
        relative: "before",
        selector: ".statusButtons"
      });
      this.addHTML(
        CCWrapper(
          CCText(
            "h4",
            "",
            `${this.verbage.mentee} Signature: `,
            CCStyling.fromObject({ width: "auto" })
          ) + CCButton("primary", "menteeSign", "Digitally Sign")
        ),
        {
          relative: "append",
          selector: ".signSection"
        },
        {
          event: "click",
          handler: () => {
            if (
              this.user.id == this.cvOwner.id &&
              this.yearData.status === "finalized"
            ) {
              const data = this.newDef.career[this.viewYear].data;
              data[this.dataPrefix + "menteeSign"] = {
                id: this.user.id,
                time: Date.now(),
                signature: this.user.name
              };
              this.update(this.struct.structure);
            }
          }
        }
      );
      this.addHTML(
        CCWrapper(
          CCText(
            "h4",
            "",
            `${this.verbage.mentor} Signature: `,
            CCStyling.fromObject({ width: "auto" })
          ) + CCButton("primary", "mentorSign", "Digitally Sign")
        ),
        {
          relative: "append",
          selector: ".signSection"
        },
        {
          event: "click",
          handler: () => {
            if (
              this.cvOwnersMentorIds.includes(this.user.id) &&
              this.yearData.status === "finalized"
            ) {
              const data = this.newDef.career[this.viewYear].data;
              data[this.dataPrefix + "mentorSign"] = {
                id: this.user.id,
                time: Date.now(),
                signature: this.user.name
              };
              this.update(this.struct.structure);
            }
          }
        }
      );
    }
  }
}
